import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import DataPill from "@components/pills/DataPill";
import LightCustomButton from "@components/buttons/LightCustomButton";
import TableActionButton from "@components/buttons/TableActionButton";
import { MerchantDropdown } from "@components/dropdowns/MerchantDropdown";
import { TextDropdown } from "@components/dropdowns/TextDropdown";
import { reportApiServices } from "@services/apis/Report";
import { RequestedReport } from "@services/models/Reports/getAllRequestedReports";
import { RequestReportRequest } from "@services/models/Reports/requestReport";
import { ColDef } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "src/redux/reducer";

const DownloadReport = () => {
  const today = new Date();

  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  const [requestedReports, setAllRequestedReports] = useState<
    RequestedReport[]
  >([]);

  const [isLoading, setIsLoading] = useState(false);

  const [isRequestingReport, setIsRequestingReport] = useState(false);

  const [selectedRequestPeriod, setSelectedRequestPeriod] = useState<
    "day" | "month"
  >("month");

  const [selectedRequestType, setSelectedRequestType] = useState<
    "transaction" | "ledger" | "cross_ramp"
  >("transaction");

  const [selectedYear, setSelectedYear] = useState<string>(
    currentYear.toString(),
  );

  const [selectedMonth, setSelectedMonth] = useState<string>(
    currentMonth.toString(),
  );

  const [selectedDay, setSelectedDay] = useState<string>(currentDay.toString());
  const userDetails = useSelector((state: AppState) => state.user);

  const merchantDetails = useMemo(
    () => [
      {
        merchantId: -1,
        merchantName: "Checking Account",
        MUUID: "",
        UUID: userDetails.userUUID,
      },
      ...userDetails.merchantDetails
        .filter((merchant) => merchant.active)
        .map((merchant) => ({
          merchantId: merchant.merchantId,
          merchantName:
            merchant.alias ?? merchant.categoryName ?? merchant.merchantName,
          MUUID: merchant.merchantUUID,
          UUID: "",
        })),
    ],
    [userDetails.merchantDetails, userDetails.userUUID],
  );

  const [selectedMerchantId, setSelectedMerchantId] = useState<number>(
    merchantDetails[0]?.merchantId || 0,
  );

  const handleMerchantSelection = (value: string | number) => {
    const selectedId = Number(value);
    setSelectedMerchantId(selectedId);
  };

  const getReportAccountParams = () => {
    if (selectedMerchantId === -1) {
      if (!userDetails.userId) {
        return null;
      }

      return { userId: userDetails.userId };
    }

    if (!selectedMerchantId) {
      return null;
    }

    return { merchantId: selectedMerchantId };
  };

  const requestPeriod = useMemo(
    () => [
      {
        id: "day",
        label: "Day",
        value: "day",
      },
      {
        id: "month",
        label: "Month",
        value: "month",
      },
    ],
    [],
  );

  const requestType = useMemo(
    () => [
      {
        id: "transaction",
        label: "Transaction Report",
        value: "transaction",
      },
      {
        id: "cross_ramp",
        label: "Cross Ramp Report",
        value: "cross_ramp",
      },
    ],
    [],
  );

  const years = useMemo(
    () => [
      {
        id: "2026",
        label: "2026",
        value: "2026",
      },
      {
        id: "2025",
        label: "2025",
        value: "2025",
      },
    ],
    [],
  );

  const months = useMemo(
    () => [
      {
        id: "1",
        label: "January",
        value: "1",
      },
      {
        id: "2",
        label: "February",
        value: "2",
      },
      {
        id: "3",
        label: "March",
        value: "3",
      },
      {
        id: "4",
        label: "April",
        value: "4",
      },
      {
        id: "5",
        label: "May",
        value: "5",
      },
      {
        id: "6",
        label: "June",
        value: "6",
      },
      {
        id: "7",
        label: "July",
        value: "7",
      },
      {
        id: "8",
        label: "August",
        value: "8",
      },
      {
        id: "9",
        label: "September",
        value: "9",
      },
      {
        id: "10",
        label: "October",
        value: "10",
      },
      {
        id: "11",
        label: "November",
        value: "11",
      },
      {
        id: "12",
        label: "December",
        value: "12",
      },
    ],
    [],
  );

  const days = useMemo(() => {
    const year = Number(selectedYear);
    const month = Number(selectedMonth);

    if (!year || !month) {
      return [];
    }

    const numberOfDays = new Date(year, month, 0).getDate();

    return Array.from({ length: numberOfDays }, (_, index) => {
      const day = (index + 1).toString();

      return {
        id: day,
        label: day,
        value: day,
      };
    });
  }, [selectedYear, selectedMonth]);

  const getAllRequestedReports = async () => {
    const accountParams = getReportAccountParams();

    if (!accountParams) {
      return;
    }

    setIsLoading(true);

    try {
      const response =
        await reportApiServices.getAllRequestedReports(accountParams);

      setAllRequestedReports(response.data);
    } catch (error) {
      console.error("Failed to fetch requested reports:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getReportDownloadUrl = async (requestId: number) => {
    const accountParams = getReportAccountParams();

    if (!accountParams) {
      return null;
    }

    try {
      const response = await reportApiServices.getReportDownloadUrl({
        requestId,
        ...accountParams,
      });

      return response.data.downloadUrl;
    } catch (error) {
      console.error("Failed to get report download URL:", error);
      return null;
    }
  };

  const handleDownloadReport = async (requestId: number) => {
    const downloadUrl = await getReportDownloadUrl(requestId);

    if (downloadUrl) {
      window.open(downloadUrl, "_blank");
    }
  };

  useEffect(() => {
    getAllRequestedReports();
  }, [selectedMerchantId]);

  const handleRequestReport = async () => {
    const accountParams = getReportAccountParams();

    if (!accountParams || !selectedYear || !selectedMonth) {
      return;
    }

    if (selectedRequestPeriod === "day" && !selectedDay) {
      return;
    }

    setIsRequestingReport(true);

    try {
      const year = Number(selectedYear);
      const month = Number(selectedMonth);

      let startDate: string;
      let endDate: string;

      if (selectedRequestPeriod === "day") {
        const day = Number(selectedDay);

        startDate = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        endDate = startDate;
      } else {
        startDate = `${year}-${String(month).padStart(2, "0")}-01`;

        const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();

        endDate = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      }

      const payload: RequestReportRequest = {
        ...accountParams,
        reportType: selectedRequestType,
        startDate,
        endDate,
      };

      await reportApiServices.requestReport(payload);

      await getAllRequestedReports();
    } catch (error) {
      console.error("Failed to request report:", error);
    } finally {
      setIsRequestingReport(false);
    }
  };

  const columnDefs = useMemo<ColDef<RequestedReport>[]>(
    () => [
      {
        headerName: "Requested At",
        width: 150,
        minWidth: 150,
        valueGetter: (params) => {
          const date = params.data?.createdAt;

          if (!date) return "";

          const formattedDate = new Date(date);

          return `${formattedDate.getDate()} ${formattedDate.toLocaleString(
            "en-US",
            {
              month: "long",
            },
          )} ${formattedDate.getFullYear()}`;
        },
      },
      {
        headerName: "Account",
        width: 180,
        minWidth: 150,
        flex: 1,
        valueGetter: (params) => {
          const merchantId = params.data?.merchantId;

          if (merchantId == null) {
            return "Checking Account";
          }

          const merchant = merchantDetails.find(
            (merchant) =>
              Number(merchant.merchantId) === Number(merchantId),
          );

          return merchant?.merchantName ?? "-";
        },
      },
      {
        field: "reportType",
        headerName: "Report Type",
        width: 130,
        minWidth: 120,
        cellRenderer: (params: any) => {
          const value = String(params.value ?? "").toLowerCase();
          const tone =
            value === "transaction"
              ? "purple"
              : value === "ledger"
                ? "blue"
                : value === "cross_ramp"
                  ? "amber"
                  : "gray";

          return (
            <div className="flex h-full items-center">
              <DataPill value={value} tone={tone} />
            </div>
          );
        },
      },
      {
        headerName: "Period",
        width: 150,
        minWidth: 140,
        valueGetter: (params) => {
          const { startDate, endDate } = params.data ?? {};

          if (!startDate) return "";

          const formatDate = (date: string) => {
            const [year, month, day] = date.split("-");

            const monthName = new Date(
              Number(year),
              Number(month) - 1,
              Number(day),
            ).toLocaleString("en-US", {
              month: "long",
            });

            return `${Number(day)} ${monthName} ${year}`;
          };

          if (startDate === endDate) {
            return formatDate(startDate);
          }

          const [year, month] = startDate.split("-");

          const monthName = new Date(
            Number(year),
            Number(month) - 1,
            1,
          ).toLocaleString("en-US", {
            month: "long",
          });

          return `${monthName} ${year}`;
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        minWidth: 110,
        cellRenderer: (params: any) => {
          const status = String(params.value ?? "").toLowerCase();
          const tone =
            status === "requested"
              ? "blue"
              : status === "processing"
                ? "amber"
                : status === "generated"
                  ? "green"
                  : status === "failed"
                    ? "red"
                    : "gray";

          return (
            <div className="flex h-full items-center">
              <DataPill value={status} tone={tone} />
            </div>
          );
        },
      },
      {
        field: "isDownloadable",
        headerName: "Download",
        minWidth: 130,
        flex: 1,
        cellRenderer: (params: any) => {
          const requestId = Number(params.data.requestId);
          const isDownloadable = Boolean(params.data.isDownloadable);
          if (!requestId || !isDownloadable) {
            return "-";
          }

          return (
            <TableActionButton
              variant="primary"
              icon="download"
              onClick={() => handleDownloadReport(requestId)}
            >
              Download
            </TableActionButton>
          );
        },
      },
    ],
    [merchantDetails, selectedMerchantId],
  );

  return (
    <div className="flex flex-col justify-center">
      <div className="h-[70px] border-b border-b-border-subtle flex items-center px-[16px]">
        <span className="text-[14px] font-medium text-text-base">
          Download Report
        </span>
      </div>
      <div className="p-[16px] min-h-[75dvh] w-full flex flex-row gap-[16px]">
        {/* Request Report */}
        <div className="flex flex-col gap-[8px] h-full min-h-0 w-[430px] shrink-0">
          <span className="text-[14px] font-medium text-text-base">
            Request Report
          </span>

          <div className="flex flex-col gap-4 border border-border-subtle rounded-[8px]">
            <div className="border-b border-b-border-subtle p-[16px]">
              <MerchantDropdown
                listOfValues={merchantDetails.map((merchant) => ({
                  id: Number(merchant.merchantId),
                  name: merchant.merchantName,
                }))}
                value={selectedMerchantId ?? undefined}
                onChange={handleMerchantSelection}
                label="Select account"
                color="bg-main-container-header"
                baseColor="bg-bg-page-body"
                borderColor="border-subtle"
                expandedBorderColor="border-button-primary"
              />
            </div>
            <div className="flex flex-row gap-[16px] w-full border-b border-b-border-subtle px-[16px] pb-[16px]">
              <div className="flex-1 min-w-0">
                <TextDropdown
                  listOfValues={requestType}
                  value={selectedRequestType}
                  onChange={(val) => {
                    const value = String(val);

                    if (value !== "transaction" && value !== "cross_ramp") {
                      return;
                    }

                    setSelectedRequestType(value);
                  }}
                  label="Report Type"
                  color=""
                  baseColor=""
                  borderColor="border-subtle"
                  enableSearch={false}
                />
              </div>

              <div className="flex-1 min-w-0">
                <TextDropdown
                  listOfValues={requestPeriod}
                  value={selectedRequestPeriod}
                  onChange={(val) => {
                    const value = String(val);

                    if (value !== "day" && value !== "month") {
                      return;
                    }

                    setSelectedRequestPeriod(value);

                    setSelectedYear(currentYear.toString());
                    setSelectedMonth(currentMonth.toString());
                    setSelectedDay(currentDay.toString());
                  }}
                  label="Report Period"
                  color=""
                  baseColor=""
                  borderColor="border-subtle"
                  enableSearch={false}
                />
              </div>
            </div>
            <div className="flex flex-row gap-[16px] w-full border-b border-b-border-subtle px-[16px] pb-[16px]">
              <div className="flex-1 min-w-0">
                <TextDropdown
                  listOfValues={years}
                  value={selectedYear}
                  onChange={(val) => {
                    const value = String(val);

                    setSelectedYear(value);

                    const maxDays = new Date(
                      Number(value),
                      Number(selectedMonth),
                      0,
                    ).getDate();

                    if (Number(selectedDay) > maxDays) {
                      setSelectedDay(maxDays.toString());
                    }
                  }}
                  label="Year"
                  color=""
                  baseColor=""
                  borderColor="border-subtle"
                  enableSearch={false}
                />
              </div>

              <div className="flex-1 min-w-0">
                <TextDropdown
                  listOfValues={months}
                  value={selectedMonth}
                  onChange={(val) => {
                    const value = String(val);

                    setSelectedMonth(value);

                    const maxDays = new Date(
                      Number(selectedYear),
                      Number(value),
                      0,
                    ).getDate();

                    if (Number(selectedDay) > maxDays) {
                      setSelectedDay(maxDays.toString());
                    }
                  }}
                  label="Month"
                  color=""
                  baseColor=""
                  borderColor="border-subtle"
                  enableSearch={false}
                />
              </div>

              {selectedRequestPeriod === "day" && (
                <div className="flex-1 min-w-0">
                  <TextDropdown
                    listOfValues={days}
                    value={selectedDay}
                    onChange={(val) => {
                      setSelectedDay(String(val));
                    }}
                    label="Day"
                    color=""
                    baseColor=""
                    borderColor="border-subtle"
                    enableSearch={false}
                  />
                </div>
              )}
            </div>
            <div className="flex w-full pb-[16px] px-[16px]">
              <LightCustomButton
                isActive={!isRequestingReport}
                onClick={handleRequestReport}
                text={isRequestingReport ? "REQUESTING..." : "REQUEST REPORT"}
                textSize="10px"
                className="h-[40px] w-full"
              />
            </div>
          </div>
        </div>

        {/* Requested Report */}
        <div
          className={`flex flex-1 flex-col gap-[8px] min-w-0 ${
            requestedReports.length > 0 ? "min-h-[75dvh]" : "h-[33.33vh]"
          }`}
        >
          <span className="text-[14px] font-medium text-text-base">
            Requested Report
          </span>

          <div className="flex-1 min-h-0 min-w-0 w-full overflow-auto">
            <AgGridReactTable
              rowData={requestedReports}
              columnDefs={columnDefs}
              animateRows={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadReport;
